import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarX, ChevronDown, Check } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useBookableEvents } from "../../../api/useBookableEvents";
import { useSearchCities, useNeighborhoods } from "@/lib/api";
import tracking from "@/lib/tracking";
import { parseUTCDate, formatTimeToDisplay } from "@/utils/dateUtils";
import { getEventTypeLabel, getEventTypeEmoji } from "@/utils/eventTypeUtils";
import { Loader } from "@/components/Loader";
import ScreenHeading from "./ScreenHeading";
import {
  HEADING_GAP,
  LIST_GAP,
  ROW_BORDER,
  ROW_SHAPE,
  ROW_SURFACE,
  ROW_SURFACE_SELECTED,
} from "../layout";
import type { EventsScreenDef, ScreenComponentProps } from "../types";

/**
 * The neighbourhood block is left-aligned to the gutter — the same edge as the
 * headline, the city selector and every night card, so the screen keeps one
 * vertical line down its left side.
 *
 * Not the row's title column, which was the first attempt: indenting to it
 * meant picking one of the row's several left edges (card, date, title) and
 * looking like it had missed the others. Here the pills align by their box and
 * the label by its text, which is what the eye wants — a pill is a box like
 * the cards above it, a label is a line of type like the headline.
 */

/**
 * Pick the first night, before the offer rather than after it.
 *
 * This screen exists because the welcome's CTA promises one — "Find my first
 * event" used to hand off to the dashboard, which has neither events nor a way
 * to book one. Keeping it inside the flow also keeps the offer that follows
 * concrete: a plan priced against a night they have already chosen is a
 * different proposition from a subscription in the abstract.
 *
 * Bookability comes from `useBookableEvents`, which is `BookingContext`'s own
 * filtering lifted out of it. The context itself cannot be reused here: it
 * owns `/booking`'s URL, so its `setSelectedEventId` navigates there on every
 * pick and it redirects there on mount for anyone already holding a booking.
 * Picking a night during signup must not eject the user from signup.
 */
export default function EventsScreen({
  def,
  rt,
}: ScreenComponentProps<EventsScreenDef>) {
  const { t } = useT();
  // The city they picked earlier in the flow, which they can change here —
  // someone signing up in one city and travelling to another is exactly the
  // person who wants to see both.
  const cityId = rt.answers.location as string | undefined;
  const { events, isLoading } = useBookableEvents(cityId);
  // `activeOnly` rather than filtering the response: a city we haven't launched
  // has no launched neighbourhoods, so picking one here could only ever produce
  // the empty state.
  const { data: citiesData } = useSearchCities("", cityId, 100, true);
  const cities = citiesData?.data || [];
  const cityName = cities.find((c) => c.id === cityId)?.name ?? "";
  const [pickingCity, setPickingCity] = useState(false);
  const shown = def.limit ? events.slice(0, def.limit) : events;
  const selectedEventId = rt.answers.selectedEventId as string | undefined;
  const selectedNeighborhoodId = rt.answers.selectedNeighborhoodId as
    | string
    | undefined;

  // Where in the city, which the reservation cannot be made without. Asked
  // here rather than handed off to the booking page afterwards: everything
  // else the booking needs is already answered by this point, and sending
  // someone who has just paid to a second screen to finish the job is how the
  // purchase and the seat came apart in the first place.
  //
  // Per event, not per city — a night only runs in the neighbourhoods it was
  // scheduled for.
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } =
    useNeighborhoods(selectedEventId || undefined, cityId || undefined);
  const neighborhoods = useMemo(
    () => neighborhoodsData?.data || [],
    [neighborhoodsData?.data],
  );

  // One neighbourhood is not a choice. Take it and move on, so only the cities
  // that genuinely branch cost the reader a decision.
  //
  // The timer lives in a ref and is cleared only on unmount. It was an inline
  // effect cleanup once, which never advanced anyone: writing the answer on
  // the previous line re-renders with `selectedNeighborhoodId` now set, the
  // dep change runs the cleanup, and the cleanup cancels the advance it had
  // just scheduled.
  const autoAdvance = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (autoAdvance.current) clearTimeout(autoAdvance.current);
    },
    [],
  );
  useEffect(() => {
    if (!selectedEventId || selectedNeighborhoodId) return;
    if (neighborhoodsLoading || neighborhoods.length !== 1) return;
    rt.setAnswer("selectedNeighborhoodId", neighborhoods[0].id);
    autoAdvance.current = setTimeout(rt.next, 260);
  }, [
    selectedEventId,
    selectedNeighborhoodId,
    neighborhoods,
    neighborhoodsLoading,
    rt,
  ]);

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading
        title={t(`${def.copyKey}.title`)}
        subtitle={t(`${def.copyKey}.subtitle`)}
      />

      {/* City first: the list underneath is meaningless without knowing which
          city it is for, and changing it re-filters rather than navigating. */}
      <div className={HEADING_GAP}>
        <button
          type="button"
          onClick={() => setPickingCity((v) => !v)}
          className={`w-full flex items-center justify-between gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE}`}
        >
          <span className="font-medium text-[15px] text-neutral-900 dark:text-neutral-50">
            {cityName || t(`${def.copyKey}.pickCity`)}
          </span>
          <ChevronDown
            className={`w-4 h-4 flex-none text-neutral-500 dark:text-neutral-400 transition-transform ${
              pickingCity ? "rotate-180" : ""
            }`}
          />
        </button>

        <AnimatePresence initial={false}>
          {pickingCity && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={{ overflow: "hidden" }}
            >
              {/* Every city we serve, which is more than fits: capped and
                  scrolled so the nights underneath stay one flick away. */}
              <div
                className={`max-h-[300px] overflow-y-auto flex flex-col ${LIST_GAP} pt-2`}
              >
                {cities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => {
                      rt.setAnswer("location", city.id);
                      // The old pick belongs to the old city's calendar.
                      rt.setAnswer("selectedEventId", undefined);
                      setPickingCity(false);
                    }}
                    className={`w-full flex-none text-left flex items-center justify-between gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${
                      city.id === cityId ? ROW_SURFACE_SELECTED : ROW_SURFACE
                    }`}
                  >
                    <span className="text-[15px] text-neutral-900 dark:text-neutral-50">
                      {city.name}
                    </span>
                    {city.id === cityId && (
                      <Check className="w-4 h-4 flex-none text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isLoading && (
        <div className="mt-4 flex justify-center py-10">
          <Loader />
        </div>
      )}

      {/* Nothing bookable is a real state, not an error: a city can be between
          sittings, or every seat within the window already taken. The dock
          lets them past either way — see the runner's `events` case — so this
          says what happened rather than trapping them behind an empty list. */}
      {!isLoading && shown.length === 0 && (
        <div className={`mt-4 flex gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE}`}>
          <CalendarX className="w-[18px] h-[18px] flex-none mt-0.5 text-neutral-500 dark:text-neutral-400" />
          <div className="text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-400">
            {t(`${def.copyKey}.empty`)}
          </div>
        </div>
      )}

      {!isLoading && shown.length > 0 && (
        <div className={`mt-4 flex flex-col ${LIST_GAP}`}>
          {shown.map((event, i) => {
            const date = parseUTCDate(event.date);
            const isSelected = selectedEventId === event.id;
            return (
              <div key={event.id} className="flex flex-col">
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.28 }}
                  onClick={() => {
                    rt.setAnswer("selectedEventId", event.id);
                    // Picking a different night invalidates where in the city:
                    // the neighbourhoods are the ones that night runs in.
                    rt.setAnswer("selectedNeighborhoodId", undefined);
                    // The screen advances itself, but only once it has what the
                    // booking needs — see the single-neighbourhood effect above
                    // and the picker below.
                  }}
                  aria-pressed={isSelected}
                  className={`w-full text-left flex items-center gap-3.5 ${ROW_BORDER} ${ROW_SHAPE} ${
                    isSelected ? ROW_SURFACE_SELECTED : ROW_SURFACE
                  }`}
                >
                  {/* The date is the thing being chosen, so it leads. */}
                  <span className="flex-none w-[46px] text-center">
                    <span className="block font-Poppins font-semibold text-[17px] leading-none text-neutral-900 dark:text-neutral-50">
                      {date.day}
                    </span>
                    <span className="block text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mt-1">
                      {date.monthShort}
                    </span>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-[15px] leading-snug text-neutral-900 dark:text-neutral-50">
                      {getEventTypeEmoji(event.type)} {getEventTypeLabel(event.type)}
                    </span>
                    <span className="block text-[13px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                      {date.weekday} · {formatTimeToDisplay(event.time)}
                    </span>
                  </span>
                </motion.button>

                {/* Under the night it belongs to, not at the foot of the
                    list: the second choice should land where the thumb already
                    is. Pills rather than full rows — it is a smaller decision
                    than the night, and stacking identical rows under a chosen
                    one made the list read as two lists fighting each other.
                    Only where the city actually branches; a single
                    neighbourhood is taken silently above. */}
                <AnimatePresence initial={false}>
                  {isSelected &&
                    !neighborhoodsLoading &&
                    neighborhoods.length > 1 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{ overflow: "hidden" }}
                      >
                        {/* Closer to the night it belongs to than to the row
                            below, so the group reads as one thing: 14 above
                            the label, 8 to its own options, and the list's
                            own 12 underneath. */}
                        <div className="pt-3.5 pb-2 text-[12px] text-neutral-500 dark:text-neutral-400">
                          {t(`${def.copyKey}.pickNeighborhood`)}
                        </div>
                        <div className="flex flex-wrap gap-2 pb-1">
                          {neighborhoods.map((n, ni) => {
                            const picked = selectedNeighborhoodId === n.id;
                            return (
                              <motion.button
                                key={n.id}
                                type="button"
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.04 * ni, duration: 0.22 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => {
                                  rt.setAnswer("selectedNeighborhoodId", n.id);
                                  setTimeout(rt.next, 260);
                                }}
                                aria-pressed={picked}
                                className={`${ROW_BORDER} rounded-full px-3.5 py-2 text-[13px] font-medium text-neutral-900 dark:text-neutral-50 transition-colors ${
                                  picked ? ROW_SURFACE_SELECTED : ROW_SURFACE
                                }`}
                              >
                                {n.name}
                              </motion.button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* The way out without choosing. A text link, never a button: someone
          with nothing in their diary yet should not be held here, but this
          must not compete with picking a night.

          It leaves for the dashboard rather than advancing to the offer:
          somebody who has just declined to pick a night is not the audience
          for a membership pitch about nights, and the events are all still
          there when they want them. */}
      <button
        type="button"
        onClick={() => {
          tracking.onboardingEscapeToEvents(rt.trackingContext, "events");
          // Through the save, not straight out — see `OnboardingRuntime.leave`.
          rt.leave("/dashboard");
        }}
        className="mt-auto pt-6 pb-2 self-center text-[13px] text-neutral-500 dark:text-neutral-400 underline underline-offset-2 hover:text-primary-500 transition-colors"
      >
        {t(`${def.copyKey}.later`)}
      </button>
    </div>
  );
}
